import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subject, Subscription, combineLatest} from 'rxjs';
import {filter, take, takeUntil, timeout} from 'rxjs/operators';
import {ChatMessage} from "../../models/chat-message";
import {ChatMessageChunk} from "../../models/chat-message-chunk";
import {WebSocketService} from '../websocket.service';
import {ConnectionOrchestrator, ConnectionState, ConnectionType, DisconnectReason} from '../connection/connection-manager.service';
import {ChatMessageProcessor} from './chat-message-processor.service';
import {ProgressUpdate} from '../../models/progress-update';
import {TelemetryService} from '../telemetry/telemetry.service';
import {LoggingService} from '../logging/logging.service';
import {WebSocketConnectionService} from '../websocket/websocket-connection.service';
import {SubscriptionCoordinator} from '../connection/subscription-coordinator.service';
import { trace } from '@opentelemetry/api';

@Injectable({
  providedIn: 'root',
})
export class ChatCommunicationService implements OnDestroy {
  private destroy$ = new Subject<void>();

  // Current operation progress
  private currentProgressSubject = new BehaviorSubject<ProgressUpdate | null>(null);

  // FIN-0079 Phase 2: Removed subscriptionsSetUp flag
  // WebSocketMessagingService now handles subscription idempotency

  // Track any active operations that need cleanup
  private activeOperationsSubscription: Subscription | null = null;
  private connectionStatusSubscription: Subscription | null = null;

  // Track connection state to prevent duplicate initialization attempts
  private isConnecting = false;

  // Message queue for reconnection state recovery
  private messageQueue: ChatMessageChunk[] = [];
  private isQueueingMessages = false;

  constructor(
    private webSocketService: WebSocketService,
    private connectionManager: ConnectionOrchestrator,
    private chatMessageProcessor: ChatMessageProcessor,
    private telemetryService: TelemetryService,
    private logger: LoggingService,
    private webSocketConnectionService: WebSocketConnectionService,
    private subscriptionCoordinator: SubscriptionCoordinator
  ) {
    this.logger.info('ChatCommunicationService', 'Service created');
  }
  
  /**
   * Get user identifier (email) from authenticated session.
   * This replaces the old userEmail/clientId logic which was unreliable (sessionStorage cleared on tab close).
   */
  private getUserIdentifier(): string {
    return this.webSocketService.getUserIdentifier();
  }

  /**
   * Public method to get user identifier
   */
  public getCurrentUserIdentifier(): string {
    return this.getUserIdentifier();
  }

  /**
   * @deprecated Use getCurrentUserIdentifier() instead. This will be removed in future versions.
   */
  public getCurrentClientId(): string {
    return this.getUserIdentifier();
  }

  /**
   * Cleanup resources when service is destroyed
   */
  ngOnDestroy(): void {
    this.logger.info('ChatCommunicationService', 'Destroying service');
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up currentProgressSubject
    this.currentProgressSubject.complete();

    // Clean up explicit subscriptions
    if (this.activeOperationsSubscription) {
      this.activeOperationsSubscription.unsubscribe();
      this.activeOperationsSubscription = null;
    }

    if (this.connectionStatusSubscription) {
      this.connectionStatusSubscription.unsubscribe();
      this.connectionStatusSubscription = null;
    }

    // FIN-0079 Phase 2: No longer need to reset flag (removed)
  }

  /**
   * Initialize connection and subscribe to chat messages
   * FIN-0079: Uses callback registration pattern to avoid deadlock
   */
  initialize(): void {
    this.logger.info('ChatCommunicationService', 'Initializing connection');

    // FIN-0079: Register initializer callback with SubscriptionCoordinator
    // This will be triggered by ConnectionOrchestrator when WebSocket is established
    this.subscriptionCoordinator.registerInitializer(
      'chat-websocket-subscriptions',
      () => this.setupWebSocketSubscription()
    );
    this.logger.info('ChatCommunicationService', '✅ Registered WebSocket subscription initializer');

    // Subscribe to connection status changes for logging
    // FIN-0079 Phase 2: No longer need to reset subscriptionsSetUp flag
    this.connectionStatusSubscription = this.connectionManager.connectionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        const userEmail = this.getUserIdentifier();
        this.logger.info('ChatCommunicationService', `Connection status: ${status.type} ${status.state}, User: ${userEmail}`);
      });

    // Subscribe to progress updates
    this.activeOperationsSubscription = this.webSocketService.operations$
      .pipe(takeUntil(this.destroy$))
      .subscribe(operations => {
        // Find the most important update to show (highest progress)
        let bestUpdate: ProgressUpdate | null = null;

        // Simple algorithm: show the update with the highest progress
        operations.forEach(progressSubject => {
          const update = progressSubject.getValue();
          if (update) {
            if (!bestUpdate || update.progress > bestUpdate.progress) {
              bestUpdate = update;
            }
          }
        });

        // Update our current progress subject with the best update
        this.currentProgressSubject.next(bestUpdate);
      });

    // No need to explicitly establish connection - let ConnectionOrchestrator handle it
  }

  /**
   * Public method to ensure subscriptions are ready before sending messages
   * FIN-0079: Now delegates to SubscriptionCoordinator to avoid circular dependency
   *
   * @deprecated This method is kept for backward compatibility but now delegates to SubscriptionCoordinator
   * @returns Promise that resolves when subscriptions are established
   * @throws Error if timeout is reached
   */
  public async ensureSubscriptionsReady(): Promise<void> {
    return this.subscriptionCoordinator.waitForSubscriptionsReady();
  }

  /**
   * Set up WebSocket subscription for chat messages
   * Prevents duplicate subscriptions that cause chunk duplication
   */
  private async setupWebSocketSubscription(): Promise<void> {
    // FIN-0079 Phase 2: Removed subscriptionsSetUp flag
    // WebSocketMessagingService now handles idempotency via stale detection

    const userEmail = this.getUserIdentifier();

    // Don't set up subscriptions without authenticated user
    if (!userEmail || userEmail === '') {
      this.logger.warn('ChatCommunicationService', 'Cannot set up WebSocket subscription without user email');
      return;
    }

    this.logger.info('ChatCommunicationService', `Setting up WebSocket subscription for user: ${userEmail}`);
    const timerName = `ws-subscription-${Date.now()}`;
    this.logger.startTimer(timerName);

    try {
      // Subscribe to personal chat topic
      const chatObservable = await this.webSocketService.subscribe<any>(`/topic/chat/${userEmail}`);
    chatObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(chunk => {
        const receiveTime = Date.now();
        const messageChunk = chunk as ChatMessageChunk;
        this.logger.info('[FRONTEND-CHAT-COMM] Received WebSocket message chunk', {
          messageId: messageChunk.messageId,
          sequenceNumber: messageChunk.sequenceNumber,
          isComplete: messageChunk.complete,
          contentLength: messageChunk.content?.length || 0,
          timestamp: messageChunk.timestamp,
          receiveTime,
          isQueueing: this.isQueueingMessages
        });

        // Check if we should queue this message instead of processing it
        if (this.isQueueingMessages) {
          this.queueMessage(messageChunk);
          return;
        }

        // Normal processing
        this.chatMessageProcessor.handleIncomingMessageChunk(messageChunk);
        this.logger.info('[FRONTEND-CHAT-COMM] Chunk processing completed', {
          messageId: messageChunk.messageId,
          sequenceNumber: messageChunk.sequenceNumber,
          processingTime: Date.now() - receiveTime + 'ms'
        });
      }, error => {
        this.logger.error('ChatCommunicationService', 'Error in chat subscription', error);
        this.telemetryService.trackError('chat_subscription_error', {error: String(error)});
      });

    // Subscribe to error topic for authentication failures
    const errorObservable = await this.webSocketService.subscribe<any>(`/topic/errors/${userEmail}`);
    errorObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        this.logger.warn('[FRONTEND-CHAT-COMM] Received error event', error);
        this.handleAuthenticationError(error);
      }, error => {
        this.logger.error('ChatCommunicationService', 'Error in error subscription', error);
      });

    // Subscribe to session topic for sessionId updates
    const sessionObservable = await this.webSocketService.subscribe<any>(`/topic/session/${userEmail}`);
    sessionObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionInfo => {
        this.logger.info('[FRONTEND-CHAT-COMM] Received session info', sessionInfo);
        this.handleSessionInfo(sessionInfo);
      }, error => {
        this.logger.error('ChatCommunicationService', 'Error in session subscription', error);
      });

      // Send subscription message for progress updates
      // Pass the user email to the server
      await this.webSocketService.send('/app/subscribe-progress', userEmail);

      this.logger.endTimer(timerName, 'ChatCommunicationService', 'WebSocket subscription establishment');

      // FIN-0079: Subscription ready signaling now handled by SubscriptionCoordinator.runAllInitializers()
      // No need to call markSubscriptionsReady() here - the coordinator will do it after all initializers complete
      this.logger.info('ChatCommunicationService', '✅ WebSocket subscriptions setup complete');

      // Track event with current trace context if available
      const span = trace.getActiveSpan();
      if (span) {
        const ctx = span.spanContext();
        this.telemetryService.trackEvent('chat_websocket_subscription_established', {userEmail: userEmail}, ctx.traceId, ctx.spanId);
      } else {
        this.telemetryService.trackEvent('chat_websocket_subscription_established', {userEmail: userEmail});
      }
    } catch (error) {
      // FIN-0079 Phase 2: No longer need to reset flag (removed)
      this.logger.error('ChatCommunicationService', 'Failed to set up WebSocket subscriptions', error);
      throw error;
    }
  }

  /**
   * Send a message using WebSocket.
   *
   * ⚠️  DESIGN BY CONTRACT - DO NOT ADD CONNECTION CHECKS HERE!
   *     See WebSocketMessagingService.send() for the contract.
   *     That method handles offline queueing - checking connection here BREAKS it.
   *     History: FIN-0100 - connection check here caused orphaned messages.
   */
  sendMessage(messages: ChatMessage[]): void {
    // Delegate directly to WebSocket layer - it handles queueing if offline
    this.logger.info('ChatCommunicationService', 'Sending message via WebSocket (will queue if disconnected)');
    this.sendMessageViaWebSocket(messages);
  }

  /**
   * Initialize a chat conversation
   * Now uses the connection manager for reliable connections
   * @param conversationId The conversation ID to use for this chat session
   */
  initializeConversation(conversationId?: string): Promise<void> {
    // Console log 1: When the method is called
    console.log('[initializeConversation] Method called at:', new Date().toISOString());
    
    // Prevent multiple concurrent initialization attempts
    if (this.isConnecting) {
      this.logger.info('ChatCommunicationService', 'Conversation initialization already in progress');
      return Promise.resolve();
    }
    
    this.isConnecting = true;
    
    return new Promise<void>(async (resolve, reject) => {
      const connectionTimeout = 8000; // 8 seconds timeout
      this.logger.info('ChatCommunicationService', 'Initializing chat conversation');

      // Console log 2: What the user identifier is
      const userEmail = this.getUserIdentifier();
      console.log('[initializeConversation] User Email:', userEmail);

      // Console log 3: Whether the WebSocket is available
      const isWebSocketAvailable = this.connectionManager.isWebSocketAvailable();
      console.log('[initializeConversation] WebSocket available:', isWebSocketAvailable);

      // Check current connection state from manager
      if (!isWebSocketAvailable) {
        this.logger.warn('ChatCommunicationService', 'WebSocket not available, cannot initialize conversation');
        this.isConnecting = false;
        reject(new Error('WebSocket connection not available'));
        return;
      }

      try {
        const payload = {
          userEmail: userEmail,
          conversationId: conversationId,
          messages: []
        };

        console.log('[initializeConversation] Attempting to send message with payload:', payload);
        await this.webSocketService.send('/app/chat', payload);
        console.log('[initializeConversation] Message sent successfully!');
        
        // Track event with current trace context if available
        const span = trace.getActiveSpan();
        if (span) {
          const ctx = span.spanContext();
          this.telemetryService.trackEvent('chat_initialized_websocket', {}, ctx.traceId, ctx.spanId);
        } else {
          this.telemetryService.trackEvent('chat_initialized_websocket');
        }
        
        this.isConnecting = false;
        resolve();
      } catch (error) {
        console.log('[initializeConversation] Failed to send message, error:', error);
        this.logger.error('ChatCommunicationService', 'Failed to send initial WebSocket message', error);
        
        // Track error with current trace context if available
        const span = trace.getActiveSpan();
        if (span) {
          const ctx = span.spanContext();
          this.telemetryService.trackEvent('chat_initialized_websocket_failure', {}, ctx.traceId, ctx.spanId);
        } else {
          this.telemetryService.trackEvent('chat_initialized_websocket_failure');
        }
        
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * Send message via WebSocket
   */
  private async sendMessageViaWebSocket(messages: ChatMessage[]): Promise<void> {
    const sendTime = Date.now();
    const senderId = `ws-send-${sendTime}`;
    this.logger.startTimer(senderId);
    
    const messageId = messages[messages.length - 1]?.messageId || 'unknown';
    this.logger.info('[FRONTEND-CHAT-COMM] Preparing to send message via WebSocket', {
      messageId,
      messageCount: messages.length,
      userEmail: this.getUserIdentifier(),
      sendTime
    });

    // Get current trace context (traceparent header)
    let traceparent: string | undefined;
    try {
      const span = trace.getActiveSpan && trace.getActiveSpan();
      if (span && span.spanContext) {
        const ctx = span.spanContext();
        // W3C traceparent format: '00-<traceId>-<spanId>-01'
        traceparent = `00-${ctx.traceId}-${ctx.spanId}-01`;
      }
    } catch (e) {
      // If OpenTelemetry is not set up, skip
      traceparent = undefined;
    }

    // Extract conversationId from the last user message (if available)
    const conversationId = messages.find(msg => !msg.bot && msg.conversationId)?.conversationId;

    const payload = {
      userEmail: this.getUserIdentifier(), // User email from JWT for routing
      conversationId: conversationId, // Include conversation ID for multi-tab filtering
      traceparent, // <-- propagate trace context
      messages: messages.map(msg => ({
        content: msg.content.getValue(),
        bot: msg.bot,
        timestamp: msg.timestamp,
        messageId: msg.messageId,
        conversationId: msg.conversationId, // Include conversation ID in each message
        tracingContext: msg.tracingContext // propagate tracingContext if present
      }))
    };

    try {
      this.logger.info('[FRONTEND-CHAT-COMM] Sending payload to WebSocket', {
        destination: '/app/chat',
        payloadSize: JSON.stringify(payload).length,
        messageId,
        userEmail: this.getUserIdentifier()
      });
      
      await this.webSocketService.send('/app/chat', payload);
      
      const sendDuration = Date.now() - sendTime;
      this.logger.endTimer(senderId, 'ChatCommunicationService', 'WebSocket message sending');
      this.logger.info('[FRONTEND-CHAT-COMM] Message sent successfully', {
        messageId,
        sendDuration: sendDuration + 'ms',
        userEmail: this.getUserIdentifier()
      });
      
      // Track event with current trace context if available
      const span = trace.getActiveSpan();
      if (span) {
        const ctx = span.spanContext();
        this.telemetryService.trackEvent('chat_message_sent_websocket', {
          messageCount: messages.length,
          payloadSize: JSON.stringify(payload).length
        }, ctx.traceId, ctx.spanId);
      } else {
        this.telemetryService.trackEvent('chat_message_sent_websocket', {
          messageCount: messages.length,
          payloadSize: JSON.stringify(payload).length
        });
      }
    } catch (error) {
      this.logger.error('ChatCommunicationService', 'Error sending message via WebSocket', error);
      this.telemetryService.trackError('chat_websocket_send_error', {error: String(error)});
      throw error;
    }
  }

  /**
   * Handle authentication errors received from the backend
   */
  private handleAuthenticationError(error: any): void {
    this.logger.error('ChatCommunicationService', 'Authentication error received', error);

    // Handle different error types
    switch (error.code) {
      case 'SESSION_EXPIRED':
        this.handleSessionExpired(error);
        break;

      case 'AUTH_FAILED':
        this.handleAuthFailed(error);
        break;

      default:
        this.logger.warn('ChatCommunicationService', `Unknown error code: ${error.code}`, error);
    }
  }

  /**
   * Handle session expiration gracefully
   */
  private handleSessionExpired(error: any): void {
    this.logger.warn('ChatCommunicationService', 'Session expired, attempting to refresh connection');

    // Hide any "Thinking..." indicators
    this.hideThinkingIndicators();

    // Show user-friendly notification (could emit event for components to handle)
    this.telemetryService.trackEvent('websocket_session_expired', {
      errorMessage: error.message
    });

    // Disconnect and reconnect to get a new session
    if (this.connectionManager.isWebSocketAvailable()) {
      this.connectionManager.disconnect(DisconnectReason.AUTH_ERROR);
      setTimeout(() => {
        this.connectionManager.establishConnection();
      }, 1000);
    }
  }

  /**
   * Handle authentication failures
   */
  private handleAuthFailed(error: any): void {
    this.logger.error('ChatCommunicationService', 'Authentication failed', error);

    // Clear any stuck "Thinking..." state
    this.hideThinkingIndicators();

    // Track the failure
    this.telemetryService.trackEvent('websocket_auth_failed', {
      errorMessage: error.message,
      action: error.action
    });

    // Could emit event for UI components to handle (show login prompt, etc.)
  }

  /**
   * Hide any "Thinking..." indicators in the UI
   */
  private hideThinkingIndicators(): void {
    // Emit event that chat components can listen to
    document.dispatchEvent(new CustomEvent('hideThinking'));
  }

  /**
   * Handle session info received from backend
   */
  private handleSessionInfo(sessionInfo: any): void {
    this.logger.info('ChatCommunicationService', 'Processing session info', sessionInfo);

    if (sessionInfo.type === 'SESSION_ESTABLISHED' && sessionInfo.sessionId) {
      // Store the sessionId in the WebSocket connection service
      this.webSocketConnectionService.setSessionId(sessionInfo.sessionId);

      this.logger.info('ChatCommunicationService', `✅ SessionId received and stored: ${sessionInfo.sessionId}`);

      // Track session establishment
      this.telemetryService.trackEvent('websocket_session_established', {
        sessionId: sessionInfo.sessionId,
        timestamp: sessionInfo.timestamp
      });
    } else {
      this.logger.warn('ChatCommunicationService', 'Invalid session info received', sessionInfo);
    }
  }

  /**
   * Start queuing messages during reconnection state recovery
   */
  startQueueingMessages(): void {
    this.isQueueingMessages = true;
    this.messageQueue = [];
    this.logger.info('ChatCommunicationService', 'Started queuing messages for state recovery');
  }

  /**
   * Stop queuing messages and return to normal processing
   */
  stopQueueingMessages(): void {
    this.isQueueingMessages = false;
    this.logger.info('ChatCommunicationService', 'Stopped queuing messages');
  }

  /**
   * Queue a message received during state recovery
   */
  private queueMessage(chunk: ChatMessageChunk): void {
    this.messageQueue.push(chunk);
    this.logger.info('ChatCommunicationService', 'Queued message during recovery', {
      messageId: chunk.messageId,
      sequenceNumber: chunk.sequenceNumber,
      queueSize: this.messageQueue.length
    });
  }

  /**
   * Process queued messages after state recovery completes.
   * Discards duplicates based on messageId.
   */
  processQueuedMessages(existingMessages: ChatMessage[]): void {
    if (this.messageQueue.length === 0) {
      this.logger.info('ChatCommunicationService', 'No queued messages to process');
      this.stopQueueingMessages();
      return;
    }

    this.logger.info('ChatCommunicationService', 'Processing queued messages', {
      queueSize: this.messageQueue.length,
      existingCount: existingMessages.length
    });

    // Build set of existing messageIds for deduplication
    const existingMessageIds = new Set<string>();
    existingMessages.forEach(msg => {
      if (msg.messageId) {
        existingMessageIds.add(msg.messageId);
      }
    });

    // Process queue, skip duplicates
    let processed = 0;
    let skipped = 0;

    this.messageQueue.forEach(chunk => {
      if (existingMessageIds.has(chunk.messageId)) {
        // Message already in state, skip
        skipped++;
        this.logger.debug('ChatCommunicationService', 'Skipping duplicate queued message', {
          messageId: chunk.messageId
        });
      } else {
        // New message, process it
        this.chatMessageProcessor.handleIncomingMessageChunk(chunk);
        processed++;
      }
    });

    this.logger.info('ChatCommunicationService', 'Queue processing complete', {
      processed,
      skipped,
      totalQueued: this.messageQueue.length
    });

    // Clear queue and stop queuing
    this.messageQueue = [];
    this.stopQueueingMessages();
  }
}